import { h, Component, Prop, State, Watch, Host, Element } from '@stencil/core';

import { getAbsoluteAssetPath, getCSSVariable } from '../../../services/utils';

import { ICONS_SET } from './helper/icons-set';
import {
  getSvgContent,
  panenkaIconContent,
  panenkaSvgIconClass,
  panenkaSvgProductIconClass,
  SPECIAL_ICONS,
  svgProductIconBackgroundColorPlaceholder,
  svgProductIconDarkColorPlaceholder,
  svgProductIconLightColorPlaceholder,
  svgProductIconOutlineColorPlaceholder,
  svgProductIconPrimaryColorPlaceholder,
  svgProductIconSecondaryColorPlaceholder,
  svgProductIconShadowColorPlaceholder,
} from './helper/request';

@Component({
  tag: 'pk-icon',
  styleUrl: './scss/pk-icon.scss',
  assetsDirs: ['svg'],
  shadow: true,
})
export class PkIcon {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkIconElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() private _svgContent: string;

  // Public Property API
  // ========================

  /** Set the stroke color of the SVG, it also acts as the primary color in products icons type.
   * The value should be a valid token of the palette color.
   * In case of multiple color icons (ex: pitch-product-icon) this color will set the light/dark color used for icon (using hsl)*/
  @Prop({ reflect: true }) color: string;

  /** Will disable the icon element */
  @Prop({ reflect: true }) disabled: boolean;

  /** Icon name to load */
  @Prop({ reflect: true }) name: string;

  /** Set the secondary color in products icons type.The value should be a valid token of the palette color. */
  @Prop({ reflect: true }) secondaryColor: string;

  /** Set the size of the SVG */
  @Prop({ reflect: true }) size: string | number;

  /** Set the shadow color in products icons type.The value should be a valid token of the palette color. */
  @Prop({ reflect: true }) shadowColor: string;

  /** Set the outline color in products icons type.The value should be a valid token of the palette color. */
  @Prop({ reflect: true }) outlineColor: string;

  /** Set the background color in products icons type.The value should be a valid token of the palette color. */
  @Prop({ reflect: true }) backgroundColor: string;

  // Prop lifecycle events
  // =======================

  @Watch('name')
  @Watch('color')
  @Watch('secondaryColor')
  @Watch('shadowColor')
  @Watch('outlineColor')
  @Watch('backgroundColor')
  handleIcon() {
    this.loadIcon();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    this.handleIcon();
  }

  componentDidLoad() {
    this.setTitleAttribute();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private loadIcon = () => {
    if (!ICONS_SET.includes(this.name?.toLowerCase())) {
      throw new Error(`😵 [Pk-Icon]: '${this.name}' is not a valid icon name`);
    }

    const colorIcon = 'color';
    const productIcon = 'product-icon';
    const url = getAbsoluteAssetPath(`./svg/${this.name}.svg`);
    this.fetchSvgContent(url, productIcon, colorIcon);
  };

  private fetchSvgContent = (url: string, productIcon: string, colorIcon: string) => {
    getSvgContent(url, true).then(() => {
      let svgContent = panenkaIconContent.get(url);

      if (!this.name.includes(productIcon)) {
        this._svgContent = this.name.includes(colorIcon)
          ? this.replaceCssClasses(svgContent, true)
          : this.replaceCssClasses(svgContent);
        return;
      }
      // Product icons are a bit bigger than system icons,
      // therefore we must increase their default size to 32 to guarantee the icons get displayed without being cut
      this.size = this.size || 32;
      // Product icons represent data information,
      // the reason why we change the default color (primary) to be `data-01`
      this.color = this.color || 'data-01';
      // and make sure to have set the secondary color to rely on `ui-03`
      this.secondaryColor = this.secondaryColor || 'ui-03';
      // Replace the primary and secondary placeholder with the corresponding token colors
      svgContent = svgContent.replaceAll(svgProductIconPrimaryColorPlaceholder, `var(--icon--color)`);
      svgContent = svgContent.replaceAll(svgProductIconSecondaryColorPlaceholder, `var(--icon--secondary-color)`);

      if (SPECIAL_ICONS.includes(this.name)) {
        // light color will always be based on this.color property. User can`'t set it.
        const lightColor = `color-mix(in srgb, white 10%, var(--pk-${this.color})`;
        // dark color will always be based on this.color property. User can`'t set it.
        const darkColor = `color-mix(in srgb, black 40%, var(--pk-${this.color}))`;
        this.shadowColor = this.shadowColor || 'ui-02';
        this.outlineColor = this.outlineColor || 'ui-05';
        this.backgroundColor = this.backgroundColor || 'background';
        // Replace colors placeholder with the corresponding token colors
        svgContent = svgContent.replaceAll(svgProductIconLightColorPlaceholder, lightColor);
        svgContent = svgContent.replaceAll(svgProductIconDarkColorPlaceholder, darkColor);
        svgContent = svgContent.replaceAll(svgProductIconShadowColorPlaceholder, `var(--icon--shadow-color)`);
        svgContent = svgContent.replaceAll(svgProductIconOutlineColorPlaceholder, `var(--icon--outline-color)`);
        svgContent = svgContent.replaceAll(svgProductIconBackgroundColorPlaceholder, `var(--icon--background-color)`);
      }

      this._svgContent =
        this.name.includes(productIcon) || SPECIAL_ICONS.includes(this.name)
          ? this.replaceCssClasses(svgContent, true)
          : this.replaceCssClasses(svgContent);
    });
  };

  private replaceCssClasses = (stringContent: string, replace: boolean = false) => {
    if (!replace) return stringContent;
    return stringContent.replace(`class="${panenkaSvgIconClass} ${panenkaSvgProductIconClass}"`, '');
  };

  private setTitleAttribute = () => {
    if (this.el.title) return;

    const title = this.name.replace(/-/g, ' ') + (this.name.includes('icon') ? '' : ' icon');
    this.el.title = title;
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.color && { ['--icon--color']: getCSSVariable(this.color) }),
      ...(this.secondaryColor && {
        ['--icon--secondary-color']: getCSSVariable(this.secondaryColor),
      }),
      ...(this.shadowColor && { ['--icon--shadow-color']: getCSSVariable(this.shadowColor) }),
      ...(this.outlineColor && {
        ['--icon--outline-color']: getCSSVariable(this.outlineColor),
      }),
      ...(this.backgroundColor && {
        ['--icon--background-color']: getCSSVariable(this.backgroundColor),
      }),
      ...(this.size && { ['--icon--width']: `${this.size}px` }),
    };

    return (
      <Host style={{ ...style }} role="img">
        <span
          class={{
            'c-icon--outline': true,
            'relative inline-flex h-[var(--icon--width)] w-[var(--icon--width)] rounded-full': true,
            'opacity-40': this.disabled,
          }}
          innerHTML={this._svgContent}
          part="outline"
        />
      </Host>
    );
  }
}
